import {
	Paragraph,
	Table,
	TableRow,
	TableCell,
	TextRun,
	PageBreak,
	WidthType,
	AlignmentType,
	BorderStyle,
	Packer,
	ITableCellBorders,
	TableLayoutType,
	Document
} from 'docx';

export const PRIMARY_COLOR = '003366';
export const FONT = 'Arial';

// Standard page width in twips (letter size 8.5in minus 1in margins = 6.5in = 9360 twips)
export const PAGE_WIDTH = 9360;

export const NO_BORDER: ITableCellBorders = {
	top: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
	bottom: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
	left: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
	right: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' }
};

export const THIN_BOTTOM_BORDER: ITableCellBorders = {
	top: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
	bottom: { style: BorderStyle.SINGLE, size: 1, color: '999999' },
	left: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
	right: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' }
};

export const THICK_BOTTOM_BORDER: ITableCellBorders = {
	top: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
	bottom: { style: BorderStyle.SINGLE, size: 3, color: PRIMARY_COLOR },
	left: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
	right: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' }
};

/**
 * Create a table cell with standard options.
 *
 * @param {string} text
 * @param {ITableCellBorders} borders
 * @param {object} [options]
 * @param {boolean} [options.bold]
 * @param {number} [options.size]
 * @param {AlignmentType} [options.alignment]
 * @return {TableCell}
 */
export function cell(text: string, borders: ITableCellBorders, options?: { bold?: boolean, size?: number, alignment?: typeof AlignmentType[keyof typeof AlignmentType] }): TableCell {
	return new TableCell({
		children: [new Paragraph({
			children: [new TextRun({ text, bold: options?.bold || false, size: options?.size || 20, font: FONT })],
			alignment: options?.alignment || AlignmentType.LEFT
		})],
		borders,
		width: { size: 0, type: WidthType.AUTO },
		margins: { marginUnitType: WidthType.DXA, top: 40, bottom: 40 }
	});
}

/**
 * Create a fixed-layout table that fills the page width.
 *
 * @param {TableRow[]} rows
 * @param {number[]} columnWidths
 * @return {Table}
 */
export function fixedTable(rows: TableRow[], columnWidths: number[]): Table {
	return new Table({
		rows,
		width: { size: PAGE_WIDTH, type: WidthType.DXA },
		columnWidths,
		layout: TableLayoutType.FIXED
	});
}

/**
 * Split the page width into N equal columns.
 *
 * @param {number} n
 * @return {number[]}
 */
export function equalColumns(n: number): number[] {
	const width = Math.floor(PAGE_WIDTH / n);
	return Array.from({ length: n }, () => width);
}

/**
 * Build a section divider heading with a bottom border.
 *
 * @param {string} title
 * @param {object} [options]
 * @param {boolean} [options.pageBreak]
 * @return {(Paragraph | Table)[]}
 */
export function sectionHeading(title: string, options?: { pageBreak?: boolean }): (Paragraph | Table)[] {
	const elements: (Paragraph | Table)[] = [];
	if (options?.pageBreak) {
		elements.push(new Paragraph({ children: [new PageBreak()] }));
	}
	elements.push(new Paragraph({ children: [new TextRun({ text: ' ', size: 12 })] }));
	return [
		...elements,
		fixedTable(
			[new TableRow({
				children: [new TableCell({
					children: [new Paragraph({
						children: [new TextRun({ text: title, bold: true, size: 24, color: PRIMARY_COLOR, font: FONT })]
					})],
					borders: {
						top: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
						bottom: { style: BorderStyle.SINGLE, size: 6, color: PRIMARY_COLOR },
						left: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' },
						right: { style: BorderStyle.NIL, size: 0, color: 'FFFFFF' }
					},
					margins: { marginUnitType: WidthType.DXA, left: 0, right: 0, top: 0, bottom: 20 }
				})]
			})],
			[PAGE_WIDTH]
		),
		new Paragraph({ children: [new TextRun({ text: ' ', size: 8 })] })
	];
}

/**
 * Fetch an image from a URL and return it as a Uint8Array.
 *
 * @param {string} url
 * @return {Promise<Uint8Array | null>}
 */
export type DocxImageType = 'png' | 'jpg' | 'bmp' | 'gif';

export async function fetchImageData(url: string): Promise<{ data: Uint8Array, type: DocxImageType } | null> {
	try {
		const response = await fetch(url);
		if (!response.ok) {
			return null;
		}
		const contentType = response.headers.get('content-type') || '';
		const type = getImageType(contentType);
		const arrayBuffer = await response.arrayBuffer();
		return { data: new Uint8Array(arrayBuffer), type };
	}
	catch {
		return null;
	}
}

/**
 * @param {string} contentType
 * @return {DocxImageType}
 */
function getImageType(contentType: string): DocxImageType {
	if (contentType.includes('jpeg') || contentType.includes('jpg')) {
		return 'jpg';
	}
	if (contentType.includes('bmp')) {
		return 'bmp';
	}
	if (contentType.includes('gif')) {
		return 'gif';
	}
	return 'png';
}

/**
 * Build a DOCX document and trigger a download.
 *
 * @param {(Paragraph | Table)[]} children
 * @param {string} filename
 * @return {Promise<void>}
 */
export async function downloadDocx(children: (Paragraph | Table)[], filename: string): Promise<void> {
	const doc = new Document({
		styles: { default: { document: { run: { font: FONT } } } },
		sections: [{ children }]
	});

	const blob = await Packer.toBlob(doc);
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	// Revoking immediately can cancel the download in some browsers, so defer until the download has started
	// (e.g. the user may take a while in a "save as" dialog), FileSaver.js uses 40s
	setTimeout(() => URL.revokeObjectURL(url), 40000);
}
